// La Biblioteca de prompts, el pop-up — ADR-2026-10-02-biblioteca-de-prompts.
//
// La misma ventana que el selector de "agregar" del Agente (`SelectorDeImagenModal`:
// centrada, 980 de ancho, esquinas de 14, entra con zoom mínimo y fundido), con la
// navegación en una columna a la IZQUIERDA bajo el título "Biblioteca de prompts":
// Historial y Favoritos arriba y, separados por una línea, los cuatro catálogos de
// fragmentos. A la derecha, el contenido:
//
//  • Historial / Favoritos: filas de texto. Tocar una REEMPLAZA el prompt. Cada fila lleva
//    una estrella para marcarla o sacarla de Favoritos.
//  • Catálogos: una grilla de cards —imagen, título y una línea de descripción, todo del
//    mismo tamaño de letra y la descripción apagada—. Tocar una SUMA su fragmento al
//    prompt (`conFragmento`) y el pop-up se queda abierto: se pueden encadenar.
//
// Es un componente propio y no `SelectorDeImagenModal` porque aquél es una grilla de
// miniaturas con pestañas arriba; lo que acá se parece es la ventana, no el adentro.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Aperture, Check, Clock, CornerDownLeft, ImageIcon, Plus, Smile, Sparkles, Star, Sun, Video, X, type LucideIcon } from 'lucide-react';
import { useExitTransition } from '../../../../hooks/useExitTransition';
import { useAgentEnv } from '../../agentEnvContext';
import { useEsEscritorio } from '../../../../chat-atlas/ui/useEsEscritorio';
import { fetchPromptHistory, type PromptHistoryTipo } from '../../promptHistory';
import {
  CATEGORIAS_DE_BIBLIOTECA,
  FRAGMENTOS,
  alternarFavorito,
  conFragmento,
  guardarFavoritos,
  imagenDeFragmento,
  leerFavoritos,
  posterDeFragmento,
  videoDeFragmento,
  type CategoriaDeBiblioteca,
  type FragmentoDePrompt,
} from './fragmentosDePrompt';

type Seccion = 'historial' | 'favoritos' | CategoriaDeBiblioteca;

const DURACION_MS = 200;

const ICONOS: Record<Seccion, LucideIcon> = {
  historial: Clock,
  favoritos: Star,
  angulos: Aperture,
  iluminacion: Sun,
  tipos: ImageIcon,
  efectos: Sparkles,
  camara: Video,
  emociones: Smile,
};

/**
 * Una card de fragmento. Con `video` (Movimientos de cámara) es un loop mudo con su poster,
 * en 16:9 como vienen los clips; si no, la imagen de `public/` en 4:3, y sin imagen (el
 * archivo no está) el título va sobre un fondo liso. Debajo, el texto a la izquierda y a la
 * derecha la pastilla "Añadir prompt", que al tocarla pasa a "Prompt añadido" (texto que
 * entra con un fundido corto y el ícono de más que se vuelve tilde). `anadido` lo decide el
 * padre mirando si el fragmento YA está en el prompt, así la pastilla dice la verdad también
 * al abrir el pop-up o si el usuario lo borró a mano. Tocar la imagen añade igual.
 */
const CardDeFragmento: React.FC<{
  categoria: CategoriaDeBiblioteca;
  fragmento: FragmentoDePrompt;
  anadido: boolean;
  onElegir: () => void;
  /** Teléfono: a media columna la pastilla "Añadir prompt" no entra al lado del título; va
   *  un botón redondo +/✓ sobre la foto y la descripción se corta en dos renglones. */
  compacta?: boolean;
}> = ({ categoria, fragmento: { id, titulo, descripcion, video }, anadido, onElegir, compacta = false }) => {
  const [sinImagen, setSinImagen] = useState(false);
  const Icono = anadido ? Check : Plus;
  if (compacta) {
    return (
      <button type="button" onClick={onElegir} aria-pressed={anadido} aria-label={`${titulo}: ${anadido ? 'añadido al prompt' : 'añadir al prompt'}`} className="flex min-w-0 flex-col text-left active:opacity-80">
        <span className={`relative block w-full overflow-hidden rounded-xl bg-[#EEF0F5] ring-1 ring-black/[0.05] ${video ? 'aspect-video' : 'aspect-[4/3]'}`}>
          {video ? (
            <video src={videoDeFragmento(categoria, id)} poster={posterDeFragmento(categoria, id)} autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />
          ) : !sinImagen && (
            <img src={imagenDeFragmento(categoria, id)} alt="" loading="lazy" onError={() => setSinImagen(true)} className="absolute inset-0 h-full w-full object-cover" />
          )}
          <span
            aria-hidden="true"
            className={`absolute bottom-2 right-2 grid h-7 w-7 place-items-center rounded-full shadow-[0_4px_12px_-4px_rgba(2,6,43,0.45)] transition-colors ${
              anadido ? 'bg-[#16A34A] text-white' : 'bg-white text-brand-black'
            }`}
          >
            <Icono key={anadido ? 'si' : 'no'} className="h-4 w-4 animate-prompt-anadido" strokeWidth={2.5} />
          </span>
        </span>
        <span className="mt-2 block truncate text-[13px] font-semibold text-brand-black/90">{titulo}</span>
        <span
          className="mt-0.5 text-[12px] leading-snug text-[#02062B]/[0.45]"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {descripcion}
        </span>
      </button>
    );
  }
  return (
    <div className="group flex min-w-0 flex-col">
      <button
        type="button"
        onClick={onElegir}
        aria-label={`${titulo}: añadir al prompt`}
        className={`relative block w-full overflow-hidden rounded-xl bg-[#EEF0F5] ring-1 ring-black/[0.05] ${video ? 'aspect-video' : 'aspect-[4/3]'}`}
      >
        {video ? (
          <video
            src={videoDeFragmento(categoria, id)}
            poster={posterDeFragmento(categoria, id)}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : !sinImagen && (
          <img
            src={imagenDeFragmento(categoria, id)}
            alt=""
            loading="lazy"
            onError={() => setSinImagen(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
        <span className="absolute inset-0 rounded-xl ring-inset ring-brand-primary transition-shadow group-hover:ring-2" aria-hidden="true" />
      </button>
      {/* Arriba, el título y la pastilla centrados en la misma línea; abajo, la descripción a todo
          el ancho. Título y descripción del MISMO tamaño; la descripción se distingue por el color. */}
      <div className="mt-3.5 flex items-center justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-brand-black/90">{titulo}</span>
        <button
          type="button"
          onClick={onElegir}
          aria-pressed={anadido}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 pl-2.5 pr-3 text-[12.5px] font-medium transition-colors ${
            anadido ? 'bg-[#16A34A14] text-[#15803D]' : 'bg-black/[0.05] text-brand-black/80 hover:bg-black/[0.08]'
          }`}
        >
          {/* `key` por estado: al cambiar se vuelven a montar y corre la animación de entrada.
              Keys DISTINTAS entre ícono y texto: con la misma, React dejaba el "+" viejo al
              lado del tilde. */}
          <Icono key={anadido ? 'icono-si' : 'icono-no'} className="h-3.5 w-3.5 animate-prompt-anadido" strokeWidth={2.5} aria-hidden="true" />
          <span key={anadido ? 'texto-si' : 'texto-no'} className="animate-prompt-anadido">{anadido ? 'Prompt añadido' : 'Añadir prompt'}</span>
        </button>
      </div>
      <span className="mt-2 block text-[13px] leading-snug text-[#02062B]/[0.45]">{descripcion}</span>
    </div>
  );
};

export const BibliotecaDePrompts: React.FC<{
  abierto: boolean;
  onCerrar: () => void;
  prompt: string;
  onPromptChange: (v: string) => void;
  /** De qué historial se lee: el de Prompt libre en imagen, el del Creador en video. */
  tipoDeHistorial: PromptHistoryTipo;
}> = ({ abierto, onCerrar, prompt, onPromptChange, tipoDeHistorial }) => {
  const { companyId } = useAgentEnv();
  const { mounted, animated } = useExitTransition(abierto, DURACION_MS);
  // En el teléfono, hoja desde abajo con las secciones en chips
  // (ADR-2026-10-13-la-biblioteca-de-prompts-en-el-telefono).
  const esEscritorio = useEsEscritorio();
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const [seccion, setSeccion] = useState<Seccion>('historial');
  // Cada vez que se abre, arranca en Historial (y la tira de pestañas al principio), aunque la
  // última vez se haya cerrado en otra (ADR-2026-10-13-la-biblioteca-abre-en-historial).
  useEffect(() => {
    if (!abierto) return;
    setSeccion('historial');
    chipsRef.current?.scrollTo({ left: 0 });
  }, [abierto]);
  const [historial, setHistorial] = useState<string[] | null>(null);
  const [favoritos, setFavoritos] = useState<string[]>(() => leerFavoritos(companyId));

  useEffect(() => {
    if (!abierto) return;
    setHistorial(null);
    let vivo = true;
    void fetchPromptHistory(companyId, tipoDeHistorial, 30).then((l) => {
      if (vivo) setHistorial(l);
    });
    setFavoritos(leerFavoritos(companyId));
    return () => {
      vivo = false;
    };
  }, [abierto, companyId, tipoDeHistorial]);

  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, onCerrar]);

  const alternar = (p: string) => {
    if (!companyId) return;
    const lista = alternarFavorito(favoritos, p);
    setFavoritos(lista);
    guardarFavoritos(companyId, lista);
  };

  // Las categorías "sólo video" (Movimientos de cámara) no se ofrecen cuando el medio es
  // imagen: un movimiento de cámara no le dice nada a una foto.
  const esVideo = tipoDeHistorial === 'video';
  const secciones = useMemo<{ id: Seccion; nombre: string; separado?: boolean }[]>(
    () => [
      { id: 'historial', nombre: 'Historial' },
      { id: 'favoritos', nombre: 'Favoritos' },
      ...CATEGORIAS_DE_BIBLIOTECA.filter((c) => esVideo || !c.soloVideo).map((c, i) => ({ id: c.id, nombre: c.nombre, separado: i === 0 })),
    ],
    [esVideo],
  );
  useEffect(() => {
    if (!secciones.some((s) => s.id === seccion)) setSeccion('historial');
  }, [secciones, seccion]);

  if (!mounted) return null;

  // Historial y Favoritos: cada prompt es una tarjeta que se toca entera para USARLO —el ícono
  // de su sección, el texto en dos renglones y una pastilla "Usar" que dice qué pasa— con la
  // estrella aparte (ADR-2026-10-13-la-biblioteca-de-prompts-se-ve-usable). Arriba, una línea
  // que lo explica. Los repetidos que sólo cambian en mayúsculas o espacios ("test" / "Test")
  // se muestran una vez.
  const filas = (lista: string[] | null, vacio: string, Icono: LucideIcon) => {
    if (lista === null) {
      return (
        <div className="flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[60px] animate-pulse rounded-2xl bg-black/[0.05]" />
          ))}
        </div>
      );
    }
    if (lista.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#1644EB]/[0.07] text-brand-primary">
            <Icono className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </span>
          <p className="max-w-[260px] text-[13.5px] text-[#02062B]/[0.5]">{vacio}</p>
        </div>
      );
    }
    const vistos = new Set<string>();
    const unicos = lista.filter((p) => {
      const clave = p.trim().replace(/\s+/g, ' ').toLowerCase();
      if (!clave || vistos.has(clave)) return false;
      vistos.add(clave);
      return true;
    });
    return (
      <>
        <p className="mb-2.5 px-1 text-[12.5px] text-[#02062B]/[0.45]">Tocá un prompt para usarlo en la caja</p>
        <ul className="flex flex-col gap-2">
          {unicos.map((p) => {
            const esFavorito = favoritos.includes(p);
            return (
              <li
                key={p}
                className="group flex items-center gap-1 rounded-2xl bg-[#F6F7FB] ring-1 ring-black/[0.04] transition-[background-color,box-shadow] hover:bg-white hover:shadow-[0_8px_22px_-14px_rgba(22,68,235,0.45)] hover:ring-[#1644EB]/20"
              >
                <button
                  type="button"
                  onClick={() => {
                    onPromptChange(p);
                    onCerrar();
                  }}
                  aria-label={`Usar el prompt: ${p}`}
                  className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-2.5 pr-1 text-left active:opacity-80"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white text-brand-primary ring-1 ring-black/[0.05]">
                    <Icono className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 text-[13.5px] leading-snug text-brand-black/90">
                    <span className="line-clamp-2">{p}</span>
                  </span>
                  <span
                    aria-hidden="true"
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1644EB]/[0.08] px-2.5 py-1 text-[12px] font-semibold text-brand-primary transition-opacity ${
                      esEscritorio ? 'opacity-60 group-hover:opacity-100' : ''
                    }`}
                  >
                    <CornerDownLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
                    Usar
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => alternar(p)}
                  aria-label={esFavorito ? 'Sacar de favoritos' : 'Guardar en favoritos'}
                  aria-pressed={esFavorito}
                  className={`mr-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                    esFavorito ? 'text-[#F5A623]' : 'text-[#02062B]/[0.3] hover:bg-black/[0.05] hover:text-[#02062B]/[0.55]'
                  }`}
                >
                  <Star className="h-4 w-4" strokeWidth={2} fill={esFavorito ? 'currentColor' : 'none'} />
                </button>
              </li>
            );
          })}
        </ul>
      </>
    );
  };

  const contenido =
    seccion === 'historial' ? (
      filas(historial, 'Todavía no escribiste ningún prompt acá. Los que uses van a aparecer en esta lista.', Clock)
    ) : seccion === 'favoritos' ? (
      filas(favoritos, 'Marcá con la estrella los prompts que quieras tener a mano.', Star)
    ) : (
      // Los fragmentos con `grupo` (Movimientos de cámara) van en bloques con un subtítulo
      // cada uno, en el orden en que aparece el primer fragmento de cada grupo.
      <div className="flex flex-col gap-6">
        {Array.from(
          FRAGMENTOS[seccion].reduce((m, f) => m.set(f.grupo ?? '', [...(m.get(f.grupo ?? '') ?? []), f]), new Map<string, FragmentoDePrompt[]>()),
        ).map(([grupo, lista]) => (
          <section key={grupo}>
            {grupo && <h5 className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-[#02062B]/[0.45]">{grupo}</h5>}
            {/* Dos columnas en todas: el movimiento de los clips se ve, y la pastilla "Añadir prompt"
                entra al lado del texto (a cuatro columnas no entraba). */}
            <div className={`grid grid-cols-2 ${esEscritorio ? 'gap-x-4 gap-y-5' : 'gap-x-3 gap-y-4'}`}>
              {lista.map((f) => (
                <CardDeFragmento
                  key={f.id}
                  compacta={!esEscritorio}
                  categoria={seccion}
                  fragmento={f}
                  anadido={prompt.toLowerCase().includes(f.prompt.toLowerCase())}
                  onElegir={() => onPromptChange(conFragmento(prompt, f.prompt))}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    );

  if (!esEscritorio) {
    const nombreActual = secciones.find((s) => s.id === seccion)?.nombre;
    return createPortal(
      <div className="fixed inset-0 z-[130]" role="dialog" aria-modal="true" aria-label="Biblioteca de prompts">
        <button
          type="button"
          aria-label="Cerrar"
          onClick={onCerrar}
          className={`absolute inset-0 h-full w-full cursor-default bg-black/45 transition-opacity duration-200 ${animated ? 'opacity-100' : 'opacity-0'}`}
        />
        <div
          className={`absolute inset-x-0 bottom-0 flex h-[88dvh] flex-col overflow-hidden rounded-t-[24px] bg-white shadow-[0_-8px_40px_rgba(2,6,43,0.18)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
            animated ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div aria-hidden="true" className="flex shrink-0 justify-center pb-1 pt-2.5">
            <span className="h-1 w-9 rounded-full bg-black/15" />
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-3 pt-1">
            <h3 className="text-[18px] font-bold tracking-[-0.01em] text-brand-black">Biblioteca de prompts</h3>
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-black/[0.05] text-brand-black/70 active:scale-95"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
          {/* Las secciones en chips que se deslizan: Historial y Favoritos, una rayita, y los
              catálogos. La elegida se trae a la vista al tocarla. */}
          <div
            ref={chipsRef}
            role="tablist"
            aria-label="Secciones de la biblioteca"
            className="scrollbar-hide flex shrink-0 scroll-px-5 items-center gap-2 overflow-x-auto px-5 pb-3"
          >
            {secciones.map((s) => {
              const Icono = ICONOS[s.id];
              const activa = s.id === seccion;
              return (
                <React.Fragment key={s.id}>
                  {s.separado && <span aria-hidden="true" className="h-5 w-px shrink-0 bg-black/10" />}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activa}
                    onClick={(e) => {
                      setSeccion(s.id);
                      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
                    }}
                    className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors ${
                      activa ? 'bg-brand-primary text-white' : 'bg-black/[0.05] text-brand-black/75 active:bg-black/[0.08]'
                    }`}
                  >
                    <Icono className="h-4 w-4 shrink-0" strokeWidth={2} />
                    {s.nombre}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
          <div className="shrink-0 border-t border-black/[0.06]" />
          {/* La hoja mide SIEMPRE lo mismo (el 88 % de la pantalla, lo que ocupa un catálogo
              largo): cambiar de pestaña no la mueve (ADR-2026-10-13-la-biblioteca-mide-siempre-lo-mismo). */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div
              key={seccion}
              className="animate-ficha-de-pestana px-4 pt-3"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}
            >
              {seccion !== 'historial' && seccion !== 'favoritos' && (
                <p className="mb-3 px-1 text-[12.5px] text-[#02062B]/[0.45]">{nombreActual}: tocá una para sumarla al prompt</p>
              )}
              {contenido}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[130] flex items-center justify-center p-4 transition-opacity duration-200 ease-out ${animated ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Biblioteca de prompts"
    >
      <button type="button" aria-label="Cerrar" onClick={onCerrar} className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]" />
      <div
        className={`relative z-10 flex h-[min(84vh,672px)] w-full max-w-[980px] overflow-hidden rounded-[14px] border border-neutral-200 bg-surface-card shadow-xl transition-[transform,opacity] duration-200 ease-out ${
          animated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Izquierda: la navegación. */}
        <nav className="flex w-[220px] shrink-0 flex-col gap-1 border-r border-black/[0.06] bg-[#FAFAFC] px-3 pb-3 pt-3.5" aria-label="Secciones de la biblioteca">
          <h3 className="mb-3 px-3 pt-0.5 text-[15px] font-semibold text-brand-black/90">Biblioteca de prompts</h3>
          {secciones.map((s) => {
            const Icono = ICONOS[s.id];
            const activa = s.id === seccion;
            return (
              <React.Fragment key={s.id}>
                {s.separado && <span className="my-2.5 block h-px bg-black/[0.07]" aria-hidden="true" />}
                <button
                  type="button"
                  onClick={() => setSeccion(s.id)}
                  aria-current={activa ? 'page' : undefined}
                  className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13.5px] font-medium transition-colors ${
                    activa ? 'bg-white text-brand-primary shadow-sm ring-1 ring-black/[0.05]' : 'text-brand-black/75 hover:bg-black/[0.04]'
                  }`}
                >
                  <Icono className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {s.nombre}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        {/* Derecha: el contenido de la sección, con su cabecera. */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-black/[0.06] px-5 pb-3 pt-3.5">
            <div className="flex min-w-0 items-baseline gap-3">
              <h4 className="text-[15px] font-semibold text-brand-black/90">
                {secciones.find((s) => s.id === seccion)?.nombre}
              </h4>
              {seccion !== 'historial' && seccion !== 'favoritos' && (
                <span className="truncate text-[12.5px] text-[#02062B]/[0.45]">Tocá una para sumarla al prompt</span>
              )}
            </div>
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-brand-black/40 transition-colors hover:bg-black/[0.06] hover:text-brand-black/70"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{contenido}</div>
        </div>

      </div>
    </div>,
    document.body,
  );
};

export default BibliotecaDePrompts;
